import React from 'react';
import * as Yup from 'yup';

import { FormProvider, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  Box,
  DialogActions,
  DialogContent,
  FormHelperText,
  Typography,
} from '@mui/material';

import { ErrorMessage } from '@hookform/error-message';
import CbButton from '../../Buttons/CbButton';

import { useAPIErrorHandler } from '../../hooks/useAPIErrorHandler';
import { PasswordField } from '../../inputs/text-fields/passwords/PasswordField';
import { withFormController } from '../../hocs/forms';
import { resetMfa } from '../../../api/AuthService';
import useNotifications from '../../../_lib/notifications/useNotifications';

interface DisableMFAProps {
  data: {
    email: string;
  };
  close: () => void;
}

export const DisableMFA = ({ data, close }: DisableMFAProps) => {
  const notifications = useNotifications();
  const handleAPIError = useAPIErrorHandler();

  const form = useForm({
    defaultValues: FormDefaultValues,
    resolver: yupResolver(FormSchema),
  });

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    return resetMfa({ username: data.email, password: formData.password })
      .then(() => {
        close();
        notifications.success('MFA reset email requested successfully!');
      })
      .catch((apiError) => {
        if (apiError.response?.status == 401) {
          return notifications.error('Invalid password');
        }

        handleAPIError(
          'Unable to request reset at this time. Please try again later'
        )(apiError);
      });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <DialogContent style={{ padding: '2rem' }}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <Typography variant="body2" className="contrast-text">
              Please enter your password in order to request an MFA reset email.
            </Typography>
          </Box>

          <Box width="25rem" margin="0 auto" marginTop="2rem">
            <ControlledPasswordField name="password" />
            <ErrorMessage
              name="password"
              render={({ message }) => (
                <FormHelperText sx={{ marginTop: 0 }}>{message}</FormHelperText>
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <CbButton onClick={close} styleName="cancel">
            Cancel
          </CbButton>
          <CbButton
            type="submit"
            loading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting || !form.formState.isValid}
            styleName="ctaButton"
          >
            Confirm
          </CbButton>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export const DisableMFAConfig = {
  DisableMFA: {
    component: DisableMFA,
    config: {
      fullWidth: true,
      maxWidth: 'sm',
      title: 'Disable MFA',
    },
  },
};

const FormDefaultValues = {
  password: '',
};

type FormValues = {
  password: string;
};

const FormSchema = Yup.object({
  password: Yup.string().required().label('Password'),
});

const ControlledPasswordField = withFormController(PasswordField);
